import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import buttonRightArrow from '../../../assets/buttons/button-arrow.svg';
import Select from 'react-select';
import infoIcon from '../../../assets/info.png';
import searchIcon from '../../../assets/search-glass.png';
import addFileIcon from '../../../assets/add-file.png';
import readXlsxFile from 'read-excel-file';
import Toaster from "../../../components/Toaster";
import Filters from "../../../components/Filters";
import Dropdown from "../../../components/FormFields/Dropdown";
import Button from "../../../components/Button";

import * as staticData from '../../../redux/staticData/actions';
import * as mappingActions from '../../../redux/Mapping/actions';
import { useEffect } from "react";
import './SetupRow.scss';
import { applyFilter } from "../../../utils/general";

const SetupRow = props => {
	const {
		listName,
		onAction,
		searchMethod,
		onChangeSearchMethod,
		listManager
	} = props;
	const dispatch = useDispatch();

	// Get related contacts state from Redux
	const hasRelatedContacts = useSelector(state => state.mapping.hasRelatedContacts);
	const contactRoleCategory = useSelector(state => state.mapping.contactRoleCategory);
	const contactRoleSegment = useSelector(state => state.mapping.contactRoleSegment);
	const contactRole = useSelector(state => state.mapping.contactRole);
	const [showTip, setShowTip] = useState();
	const [isPasting, setIsPasting] = useState(false);
	const [selectedReportLogic, setSelectedReportLogic] = useState('AND');
	const [search, setSearch] = useState('');
	const [file, setFile] = useState(null);
	const [regions, setRegions] = useState([]);
	const [segments, setSegments] = useState([]);
	const [selectedVendor, setSelectedVendor] = useState('aws');
	const [regionItems, setRegionItems] = useState([]);
	const [segmentItems, setSegmentItems] = useState([]);
	const [dropdownData, setDropdownData] = useState(null);
	const [awsSegmentItems, setAwsSegmentItems] = useState([]);
	const [msSegmentItems, setMsSegmentItems] = useState([]);
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [selectedFilters, setSelectedFilters] = useState([]);
	const isChangingVendor = useRef(false);

	useEffect(() => {
		dispatch(staticData.loadStaticData((data) => {
			console.log('data', data);
			setDropdownData(data);
			setRegionItems(data.region.map(r => ({ value: r.id, label: r.name })));
			setAwsSegmentItems(data.awsSegment.map(s => ({ value: s.id, label: s.name })));
			setMsSegmentItems(data.segmentGroup.map(s => ({ value: s.id, label: s.name })));
			setSegmentItems(data.awsSegment.map(s => ({ value: s.id, label: s.name })));
		}));
	}, []);

	// Debounce search input
	useEffect(() => {
		if (isChangingVendor.current) {
			isChangingVendor.current = false;
			return;
		}

		const handler = setTimeout(() => {
			setDebouncedSearch(search);
		}, 500);
		return () => {
			clearTimeout(handler);
		};
	}, [search]);

	// Auto-trigger search when debounced search changes (500ms delay)
	useEffect(() => {
		if (searchMethod === 'manual') {
			const accounts = search.split("\n").filter(n => n.length > 0);

			let currentSegmentData = selectedVendor === 'aws'
				? (dropdownData?.awsSegment || [])
				: (dropdownData?.segmentGroup || []);
			currentSegmentData = currentSegmentData.map(s => ({ ...s, segments: s.id }));
			const regionsData = dropdownData?.region.map(r => ({ ...r, regions: r.id })) || [];

			let regionsFilter = [];
			let segmentsFilter = [];

			selectedFilters.forEach(filter => {
				segmentsFilter = applyFilter(currentSegmentData, filter);
				regionsFilter = applyFilter(regionsData, filter);
			});

			onAction({
				type: 'manual',
				accounts,
				regions: regionsFilter.map(r => r.id),
				segments: segmentsFilter.map(s => s.id),
			});
		}
	}, [debouncedSearch, searchMethod]);

	// Auto-trigger search when regions, segments, or filters change (no debounce - immediate)
	useEffect(() => {
		if (isChangingVendor.current) {
			isChangingVendor.current = false;
			return;
		}

		if (searchMethod === 'manual') {
			const accounts = search.split("\n").filter(n => n.length > 0);

			let currentSegmentData = selectedVendor === 'aws'
				? (dropdownData?.awsSegment || [])
				: (dropdownData?.segmentGroup || []);
			currentSegmentData = currentSegmentData.map(s => ({ ...s, segments: s.id }));
			const regionsData = dropdownData?.region.map(r => ({ ...r, regions: r.id })) || [];

			let regionsFilter = [];
			let segmentsFilter = [];

			selectedFilters.forEach(filter => {
				segmentsFilter = applyFilter(currentSegmentData, filter);
				regionsFilter = applyFilter(regionsData, filter);
			});

			onAction({
				type: 'manual',
				accounts,
				regions: regionsFilter.map(r => r.id),
				segments: segmentsFilter.map(s => s.id),
			});
		}
	}, [selectedFilters]);

	const hiddenFileInput = React.useRef(null);

	const editableDiv = useRef();

	const resetView = () => {
		setFile(null);
		setSearch('');
		if (editableDiv && editableDiv.current) {
			editableDiv.current.innerHTML = '';
		}
		onAction({ type: 'reset' });
	};

	const updateSearchMethod = type => {
		onChangeSearchMethod(type);
		onAction({ type: 'reset' });
	};

	const updateSelectedVendor = (vendor) => {
		setSelectedVendor(vendor);

		if (vendor === 'aws') {
			setSegmentItems(awsSegmentItems);
		}
		else {
			setSegmentItems(msSegmentItems);
		}

		// Set flag to skip useEffect
		isChangingVendor.current = true;

		setSearch('');
		if (editableDiv && editableDiv.current) {
			editableDiv.current.innerHTML = '';
		}
		setRegions([]);
		setSegments([]);
		setSelectedFilters([]);

		onAction({ type: 'reset', vendor });
	};

	const updateReportLogic = logic => {
		setSelectedReportLogic(logic === 'AND' ? 'OR' : 'AND');
	};

	const handleFileChange = file => {
		setFile(file);
	};

	const onFileClick = () => {
		hiddenFileInput.current.click();
	};

	const onFileChange = event => {
		const fileUploaded = event.target.files[0];
		handleFileChange(fileUploaded);
	};

	const loadExcel = () => {
		if (!file) {
			Toaster.showWarn(`Please select a file.`);
			return;
		}
		readXlsxFile(file)
			.then(async (rows) => {
				const max = 3500;
				if (rows.length > max) {
					Toaster.showWarn(`Please upload up to ${max} accounts. (your file contains ${rows.length}).`);
					return;
				}

				// `rows` is an array of rows
				const accountNames = [];

				rows.forEach(row => {
					if (row[0] != null && row[0] !== "") {
						const name = row[0].toLowerCase().trim();
						accountNames.push(name);
					}
				});

				// resetView();
				// setLoading(true);
				// await dispatch(userActions.searchAccounts(selectedVendor, accountNames, searchLoaded));
				onAction({ type: 'file', accounts: accountNames, fileName: file.name, regions, segments });
			})
			.catch(e => {
				Toaster.showWarn(`Could not load the file. Maybe you left it open? Try to close and then pick it again.`);
				console.log("ERROR: ", e);
			})
	};


	const handlePaste = (e) => {
		setIsPasting(true);
	};

	const handleInput = (e) => {
		const text = e.target.innerText;

		if (isPasting) {
			setIsPasting(false);
			e.target.innerHTML = text.replaceAll("\n", "<br/>");
		}

		setSearch(text);

		if (text !== '') {
			e.target.removeAttribute('data-placeholder');
		}
		else {
			e.target.setAttribute('data-placeholder', 'Search accounts...');
		}
	};

	const selectStyles = {
		control: (provided, state) => ({
			...provided,
			width: '250px',
			borderRadius: '30px',
			border: '1px solid #6A8991',
			outline: 'none',
			padding: '0 5px'
		})
	};

	const relatedContactsSelectStyles = {
		control: (baseStyles, state) => ({
			...baseStyles,
			border: '1px solid #D3E1ED !important',
			borderShadow: '0px 0px 0px 0px transparent',
			borderRadius: '45px',
			backgroundColor: 'transparent',
			paddingLeft: '5px',
			minHeight: '33px',
			outline: 'none',
			overflow: 'hidden',
		}),
		indicatorsContainer: (baseStyles, state) => ({
			...baseStyles,
			minHeight: '33px',
		}),
		valueContainer: (baseStyles, state) => ({
			...baseStyles,
			minHeight: '33px',
		}),
		multiValue: (baseStyles, state) => ({
			...baseStyles,
			color: '#6A8991',
			fontSize: '14px',
		}),
	};

	// Get role segment options based on vendor
	const getRoleSegmentOptions = () => {
		if (!dropdownData) return [];
		if (selectedVendor === 'aws') {
			return dropdownData.awsSegment?.map(s => ({ value: s.id, label: s.name })) || [];
		} else {
			return dropdownData.segment?.map(s => ({ value: s.id, label: s.name })) || [];
		}
	};

	// Get role category options from staticData
	const getRoleCategoryOptions = () => {
		if (!dropdownData) return [];
		return dropdownData.roleCategories?.map(c => ({ value: c.id, label: c.name })) || [];
	};

	// Get contact role options from staticData
	const getContactRoleOptions = () => {
		if (!dropdownData) return [];
		return dropdownData.contactRoles?.map(r => ({ value: r.id, label: r.name })) || [];
	};

	const roleCategoryOptions = getRoleCategoryOptions();
	const roleSegmentOptions = getRoleSegmentOptions();
	const roleOptions = getContactRoleOptions();

	const onToggleHasRelated = (checked) => {
		dispatch(mappingActions.setHasRelatedContacts(checked));
		if (!checked) {
			dispatch(mappingActions.resetRelatedContacts());
		}
	};

	// Handle searchMethod changes that require onAction calls
	useEffect(() => {
		if (searchMethod === 'list') {
			onAction({ type: 'list' });
		} else if (searchMethod === 'report') {
			onAction({ type: 'report', logic: selectedReportLogic });
		}
	}, [searchMethod, selectedReportLogic]);

	const renderSelectedOptionFields = () => {
		switch (searchMethod) {
			case 'upload':
				return (
					<>
						<div onClick={onFileClick} className="setup-row-file-input">
							<img src={addFileIcon} alt="" />
						</div>

						<div onClick={onFileClick} className="setup-row-file-input">
							{file ? file.name : 'Upload File'}
						</div>

						<input type="file" ref={hiddenFileInput} style={{ display: 'none' }} onChange={onFileChange} />

						<Select styles={selectStyles} isMulti={true} width={200} options={regionItems} onChange={setRegions} value={regions} placeholder="Across All Regions" />
						{/*<Dropdown width={182} items={regionItems} topOption={{key: '', label: 'Across All Regions'}} onChange={updateRegions} selectedValue={region} keyField="key" labelField="label" />*/}

						<Select styles={selectStyles} isMulti={true} width={200} options={segmentItems} onChange={setSegments} value={segments} placeholder="Across All Segments" />
						{/*<Dropdown width={182} items={segmentItems} topOption={{key: '', label: 'Across All Segments'}} onChange={updateSegments} selectedValue={segment} keyField="key" labelField="label" />*/}

						<Button type="blue" width={50} rightIcon={buttonRightArrow} onClick={loadExcel}>Go</Button>

						<div style={{ position: 'relative' }}>
							<img src={infoIcon} alt="" onMouseOver={() => setShowTip('manual')} onMouseOut={() => setShowTip(null)} />
							<div className="setup-row-tooltip" style={{ opacity: showTip === 'manual' ? 1 : 0 }}>
								<div style={{ fontSize: '12px', color: '#40A0C1', paddingBottom: '5px' }}>Required format: Excel file with 1 sheet, with list</div>
								<div style={{ fontSize: '12px', color: '#40A0C1', paddingBottom: '5px' }}>of account names in column A, without header.</div>
							</div>
						</div>
					</>
				);
			case 'manual':
				return (
					<>
						<img src={searchIcon} alt="" />
						<div
							ref={editableDiv}
							contentEditable
							className="setup-row-manual-search-input"
							onInput={handleInput}
							onPaste={handlePaste}
							suppressContentEditableWarning={true}
							data-placeholder="Search accounts..." />
						<Filters
							availableFilters={availableFilters}
							selectedFilters={selectedFilters}
							onAddFilter={addFilter}
							onRemoveFilter={removeFilter}
							clearFilters={clearFilters}
						/>
					</>
				);
			case 'list':
			case 'report':
				return null; // Let useEffect handle onAction calls
		}
	};

	const types = [
		{ key: 'manual', label: 'Manual' },
		{ key: 'upload', label: 'Upload File' },
		{ key: 'report', label: 'Report' },
		{ key: 'separator', label: '----------------', disabled: true },
		{ key: 'list', label: 'Saved List' }
	];

	// Get segment data based on selected vendor
	const currentSegmentData = selectedVendor === 'aws'
		? (dropdownData?.awsSegment || [])
		: (dropdownData?.segmentGroup || []);

	const availableFilters = dropdownData ? [
		{ label: 'Region', key: 'regions', type: 'list', values: dropdownData.region || [] },
		{ label: 'Segment', key: 'segments', type: 'list', values: currentSegmentData },
	] : [];

	// Handler functions for Filters component
	const addFilter = filter => {
		const index = selectedFilters.findIndex(f => f.name === filter.name);
		if (index < 0) {
			setSelectedFilters([...selectedFilters, filter]);
		} else {
			const updated = [...selectedFilters];
			updated[index] = filter;
			setSelectedFilters(updated);
		}
	};

	const removeFilter = filterToRemove => {
		const filters = selectedFilters.filter(f => f.name !== filterToRemove.name);
		setSelectedFilters(filters);
	};

	const clearFilters = () => {
		setSelectedFilters([]);
	};

	const typeDropdownWidth = (searchMethod === 'upload' || searchMethod === 'list') ? 110 : 90;

	return (
		<div className="setup-row-wrapper">
			<div className="setup-row-container">
				<Dropdown theme="dark" width={typeDropdownWidth} items={types} onChange={updateSearchMethod} selectedValue={searchMethod} keyField="key" labelField="label" />

				{
					searchMethod !== 'list' &&
					<div className="setup-row-vendor-dropdown">
						<Dropdown
							theme="dark"
							width={90}
							items={[
								{ key: 'aws', label: 'AWS' },
								{ key: 'ms', label: 'MS' }
							]}
							onChange={updateSelectedVendor}
							selectedValue={selectedVendor}
							keyField="key"
							labelField="label"
						/>
					</div>
				}
				{
					listName && <div style={{ color: '#6A8991' }}>Selected List: <b>{listName}</b></div>
				}

				{
					searchMethod === 'report' &&
					<>
						{/* Has Related Contacts checkbox and filters */}
						<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
							<label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
								<input type="checkbox" checked={hasRelatedContacts} onChange={e => onToggleHasRelated(e.target.checked)} />
								<span style={{ fontSize: 16, fontWeight: 700, color: '#6A8991', whiteSpace: 'nowrap' }}>Has Related Contacts</span>
							</label>

							{hasRelatedContacts && (
								<>
									<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
										<span style={{ fontSize: 14, color: '#6A8991' }}>Category:</span>
										<div style={{ minWidth: '250px', textAlign: 'left' }}>
											<Select
												isClearable
												isMulti
												closeMenuOnSelect={false}
												styles={relatedContactsSelectStyles}
												options={roleCategoryOptions}
												value={contactRoleCategory}
												onChange={v => dispatch(mappingActions.setContactRoleCategory(v || []))}
												placeholder="Select..."
											/>
										</div>
									</div>
									<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
										<span style={{ fontSize: 14, color: '#6A8991' }}>Segment:</span>
										<div style={{ minWidth: '250px', textAlign: 'left' }}>
											<Select
												isClearable
												isMulti
												closeMenuOnSelect={false}
												styles={relatedContactsSelectStyles}
												options={roleSegmentOptions}
												value={contactRoleSegment}
												onChange={v => dispatch(mappingActions.setContactRoleSegment(v || []))}
												placeholder="Select..."
											/>
										</div>
									</div>
								</>
							)}
						</div>
					</>
				}

				{renderSelectedOptionFields()}
			</div>

			{listManager && (
				<div style={{ marginLeft: 'auto', minWidth: '120px', marginLeft: '10px' }}>
					{listManager}
				</div>
			)}
		</div>
	)
};

export default SetupRow;
